import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Tabs,
  Tab,
  Spinner,
  useDisclosure,
  Divider,
  Chip,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Edit2,
  ArrowLeft,
  Plus,
  FileText,
  ShoppingBag,
  Trash2,
  Calendar,
  CreditCard,
} from "lucide-react";

import { customersService } from "@/services/customersService";
import { ordersService } from "@/services/ordersService";
import { Customer, CustomerAddress, CustomerUpdate } from "@/types/customers";
import { Order } from "@/types/orders";
import CustomerFormModal from "@/components/customers/CustomerFormModal";
// import AddressFormModal from "@/components/customers/AddressFormModal";
import { formatCurrency, formatDate, formatPhone } from "@/utils/formatters";

const CustomerDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const customerId = parseInt(id || "0");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerOrders, setCustomerOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTab, setSelectedTab] = useState("info");

  // Edit customer modal
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  // Add address modal
  const {
    // isOpen: isAddressOpen,
    onOpen: onAddressOpen,
    // onClose: onAddressClose,
  } = useDisclosure();

  // Delete address modal
  const {
    isOpen: isDeleteAddressOpen,
    onOpen: onDeleteAddressOpen,
    onClose: onDeleteAddressClose,
  } = useDisclosure();
  const [selectedAddress, setSelectedAddress] =
    useState<CustomerAddress | null>(null);
  const [deletingAddress, setDeletingAddress] = useState(false);

  useEffect(() => {
    if (customerId) {
      fetchCustomerData();
    }
  }, [customerId]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      setError("");

      const customerData = await customersService.getCustomerById(customerId);

      setCustomer(customerData);

      fetchCustomerOrders();
    } catch (err) {
      console.error("Error fetching customer:", err);
      setError("Failed to load customer data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerOrders = async () => {
    try {
      setOrdersLoading(true);

      const orders = await ordersService.getOrders({
        customer_id: customerId,
        limit: 5,
      });

      setCustomerOrders(orders.orders);
    } catch (err) {
      console.error("Error fetching customer orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleEditCustomer = () => {
    onEditOpen();
  };

  const handleAddAddress = () => {
    setSelectedAddress(null);
    onAddressOpen();
  };

  const handleEditAddress = (address: CustomerAddress) => {
    setSelectedAddress(address);
    onAddressOpen();
  };

  const handleDeleteAddress = (address: CustomerAddress) => {
    setSelectedAddress(address);
    onDeleteAddressOpen();
  };

  const confirmDeleteAddress = async () => {
    if (!selectedAddress) return;

    try {
      setDeletingAddress(true);
      await customersService.deleteAddress(selectedAddress.id);

      // Refresh customer data
      fetchCustomerData();
      onDeleteAddressClose();
    } catch (err) {
      console.error("Error deleting address:", err);
      setError("Failed to delete address");
    } finally {
      setDeletingAddress(false);
    }
  };

  const handleCustomerUpdated = async (customerData: CustomerUpdate) => {
    await customersService.updateCustomer(customerId, customerData);

    for (const address of customerData.addresses || []) {
      await customersService.updateAddress(address.id, address);
    }
    // Refresh the customer data
    fetchCustomerData();
    onEditClose();
  };

  // const handleAddressUpdated = () => {
  //   fetchCustomerData();
  //   onAddressClose();
  // };

  const handleBackClick = () => {
    navigate("/customers");
  };

  const handleCreateOrder = () => {
    navigate(`/orders/new?customerId=${customerId}`);
  };

  const handleViewAllOrders = () => {
    navigate(`/orders?customerId=${customerId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="p-8">
        <div className="text-center p-6 border border-danger-200 rounded-lg">
          <h2 className="text-xl text-danger mb-2">Error</h2>
          <p>{error || "Failed to load customer data"}</p>
          <Button className="mt-4" color="primary" onPress={handleBackClick}>
            Back to Customers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button isIconOnly variant="light" onPress={handleBackClick}>
            <ArrowLeft size={16} />
          </Button>
          <h1 className="text-2xl font-bold">
            {customer.first_name} {customer.last_name}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            color="primary"
            startContent={<Edit2 size={16} />}
            variant="flat"
            onPress={handleEditCustomer}
          >
            Edit
          </Button>
          <Button
            color="primary"
            startContent={<ShoppingBag size={16} />}
            onPress={handleCreateOrder}
          >
            New Order
          </Button>
        </div>
      </div>

      <Tabs
        selectedKey={selectedTab}
        onSelectionChange={(key) => setSelectedTab(key as string)}
      >
        <Tab key="info" title="Info">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
            {/* Customer Info Card */}
            <Card className="p-4 space-y-4 col-span-1">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Customer Info</h2>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={handleEditCustomer}
                >
                  <Edit2 size={16} />
                </Button>
              </div>

              <Divider />

              <div className="space-y-4">
                <div className="flex gap-2 items-center">
                  <User className="w-5 h-5 text-default-500" />
                  <div>
                    <div className="font-medium">
                      {customer.first_name} {customer.last_name}
                    </div>
                    <div className="text-small text-default-500">Name</div>
                  </div>
                </div>

                <div className="flex gap-2 items-center">
                  <Phone className="w-5 h-5 text-default-500" />
                  <div>
                    <div className="font-medium">
                      {formatPhone(customer.phone_number)}
                    </div>
                    <div className="text-small text-default-500">Phone</div>
                  </div>
                </div>

                {customer.email && (
                  <div className="flex gap-2 items-center">
                    <Mail className="w-5 h-5 text-default-500" />
                    <div>
                      <div className="font-medium">{customer.email}</div>
                      <div className="text-small text-default-500">Email</div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 items-center">
                  <Calendar className="w-5 h-5 text-default-500" />
                  <div>
                    <div className="font-medium">
                      {formatDate(customer.created_at)}
                    </div>
                    <div className="text-small text-default-500">
                      Customer Since
                    </div>
                  </div>
                </div>

                {customer.last_order_date && (
                  <div className="flex gap-2 items-center">
                    <ShoppingBag className="w-5 h-5 text-default-500" />
                    <div>
                      <div className="font-medium">
                        {formatDate(customer.last_order_date)}
                      </div>
                      <div className="text-small text-default-500">
                        Last Order
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Divider />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="text-small text-default-500">
                    Total Orders
                  </div>
                  <div className="text-xl font-semibold">
                    {customer.total_orders}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-small text-default-500">Total Spent</div>
                  <div className="text-xl font-semibold">
                    {formatCurrency(customer.total_spent)}
                  </div>
                </div>
              </div>

              {customer.notes && (
                <>
                  <Divider />
                  <div className="space-y-2">
                    <div className="text-small text-default-500">Notes</div>
                    <div className="p-2 bg-default-100 rounded-md text-sm">
                      {customer.notes}
                    </div>
                  </div>
                </>
              )}
            </Card>

            {/* Addresses Card */}
            <Card className="p-4 space-y-4 col-span-1 lg:col-span-2">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Delivery Addresses</h2>
                <Button
                  color="primary"
                  size="sm"
                  startContent={<Plus size={16} />}
                  onPress={handleAddAddress}
                >
                  Add Address
                </Button>
              </div>

              <Divider />

              {customer.addresses && customer.addresses.length > 0 ? (
                <div className="space-y-4">
                  {customer.addresses.map((address) => (
                    <div key={address.id} className="p-3 border rounded-md">
                      <div className="flex justify-between">
                        <div className="flex gap-2">
                          <MapPin className="w-5 h-5 text-default-500" />
                          <div>
                            <div className="font-medium">
                              {address.address_line1}
                              {address.is_default && (
                                <Chip
                                  className="ml-2"
                                  color="primary"
                                  size="sm"
                                >
                                  Default
                                </Chip>
                              )}
                            </div>
                            {address.address_line2 && (
                              <div className="text-sm">
                                {address.address_line2}
                              </div>
                            )}
                            <div className="text-sm">
                              {address.city}, {address.state} {address.zip_code}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() => handleEditAddress(address)}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            isIconOnly
                            color="danger"
                            size="sm"
                            variant="light"
                            onPress={() => handleDeleteAddress(address)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>

                      {address.delivery_instructions && (
                        <div className="mt-2 pl-7">
                          <div className="text-xs text-default-500">
                            Delivery Instructions:
                          </div>
                          <div className="text-sm">
                            {address.delivery_instructions}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-6 border border-dashed rounded-md">
                  <MapPin className="mx-auto h-8 w-8 text-default-400" />
                  <p className="mt-2 text-default-500">
                    No addresses found. Add an address to enable delivery.
                  </p>
                  <Button
                    className="mt-4"
                    color="primary"
                    size="sm"
                    startContent={<Plus size={16} />}
                    onPress={handleAddAddress}
                  >
                    Add Address
                  </Button>
                </div>
              )}
            </Card>

            {/* Recent Orders Card */}
            <Card className="p-4 space-y-4 col-span-1 lg:col-span-3">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Recent Orders</h2>
                <div className="flex gap-2">
                  <Button
                    color="primary"
                    size="sm"
                    startContent={<FileText size={16} />}
                    variant="flat"
                    onPress={handleViewAllOrders}
                  >
                    View All Orders
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    startContent={<ShoppingBag size={16} />}
                    onPress={handleCreateOrder}
                  >
                    New Order
                  </Button>
                </div>
              </div>

              <Divider />

              {ordersLoading ? (
                <div className="flex justify-center items-center p-6">
                  <Spinner size="md" />
                </div>
              ) : customerOrders.length > 0 ? (
                <Table aria-label="Recent orders">
                  <TableHeader>
                    <TableColumn>ORDER #</TableColumn>
                    <TableColumn>DATE</TableColumn>
                    <TableColumn>TYPE</TableColumn>
                    <TableColumn>STATUS</TableColumn>
                    <TableColumn>PAYMENT</TableColumn>
                    <TableColumn>TOTAL</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {customerOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          <Button
                            as="a"
                            className="p-0 text-primary underline"
                            href={`/orders/${order.id}`}
                            variant="light"
                          >
                            #{order.order_number}
                          </Button>
                        </TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                        <TableCell>
                          <Chip size="sm" variant="flat">
                            {order.order_type === "pickup"
                              ? "Pickup"
                              : "Delivery"}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            color={
                              order.status_name.toLowerCase() === "cancelled"
                                ? "danger"
                                : order.status_name.toLowerCase() ===
                                      "delivered" ||
                                    order.status_name.toLowerCase() ===
                                      "picked_up"
                                  ? "success"
                                  : "primary"
                            }
                            size="sm"
                            variant="flat"
                          >
                            {order.status_name}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <CreditCard size={14} />
                            <span>{order.payment_method_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {formatCurrency(order.total)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center p-6 border border-dashed rounded-md">
                  <ShoppingBag className="mx-auto h-8 w-8 text-default-400" />
                  <p className="mt-2 text-default-500">
                    No orders found for this customer.
                  </p>
                  <Button
                    className="mt-4"
                    color="primary"
                    size="sm"
                    startContent={<ShoppingBag size={16} />}
                    onPress={handleCreateOrder}
                  >
                    Create Order
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </Tab>

        <Tab key="orders" title="Order History">
          {/* Order history would be implemented here */}
          <div className="mt-4">
            <Card className="p-4">
              <div className="text-center p-6">
                <p>Full order history implementation would go here.</p>
                <p className="text-default-500">
                  The recent orders section shows a preview of the most recent
                  orders.
                </p>
              </div>
            </Card>
          </div>
        </Tab>
      </Tabs>

      {/* Edit Customer Modal */}
      {isEditOpen && (
        <CustomerFormModal
          customer={customer}
          onCancel={onEditClose}
          onSave={handleCustomerUpdated}
          loading={loading}
        />
      )}

      {/* Add/Edit Address Modal */}
      {/* <AddressFormModal
        isOpen={isAddressOpen}
        onClose={onAddressClose}
        onSuccess={handleAddressUpdated}
        address={selectedAddress}
        customerId={customer.id}
      /> */}

      {/* Delete Address Confirmation Modal */}
      <Modal isOpen={isDeleteAddressOpen} onClose={onDeleteAddressClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Delete Address</ModalHeader>
              <ModalBody>
                <p>Are you sure you want to delete this address?</p>
                {selectedAddress && (
                  <div className="mt-2 p-3 bg-default-100 rounded-md">
                    <p>{selectedAddress.address_line1}</p>
                    {selectedAddress.address_line2 && (
                      <p>{selectedAddress.address_line2}</p>
                    )}
                    <p>
                      {selectedAddress.city}, {selectedAddress.state}{" "}
                      {selectedAddress.zip_code}
                    </p>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  isLoading={deletingAddress}
                  onPress={confirmDeleteAddress}
                >
                  Delete
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CustomerDetailsPage;
